import React from 'react';

import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from 'react-table';

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div>
      Search:{' '}
      <div className="d-flex justify-content-start align-items-center">
        <i
          className="bi bi-search ms-3"
          style={{
            position: 'absolute',
          }}
        ></i>
        <input
          className="form-control"
          style={{
            paddingLeft: '42px',
          }}
          value={value || ''}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={`Search in ${count} records...`}
        />
      </div>
    </div>
  );
}

const getUpdatedMultiSelectRows = (rowId, multiSelectRows) => {
  const newMultiSelectRows = [...multiSelectRows];
  const index = newMultiSelectRows.indexOf(rowId);
  if (index === -1) {
    newMultiSelectRows.push(rowId);
  } else {
    newMultiSelectRows.splice(index, 1);
  }
  return newMultiSelectRows;
};

const getAllRowsID = (data) => {
  return data.map((i) => i.id);
};

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      className="form-control"
      value={filterValue || ''}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

function Table({
  columns,
  data,
  onRowClick,
  showClickableRows,
  tooltipText,
  topContent,
  topContentAlignment,
  topContentColRatioLeft,
  topContentColRatioRight,
  topContentColRatioLeftMd,
  topContentColRatioRightMd,
  showMultiSelect,
  multiSelectRows,
  setMultiSelectRows,
  underSearchBarContent,
}) {
  const defaultColumn = React.useMemo(
    () => ({
      // Default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  return (
    <div>
      <div
        className={`row align-items-center ${
          topContentAlignment && topContentAlignment.toLowerCase() === 'right'
            ? ''
            : 'flex-row-reverse'
        }`}
      >
        <div
          className={
            topContent
              ? topContentColRatioLeftMd
                ? `col-md-${
                    topContentColRatioLeftMd ? topContentColRatioLeftMd : '6'
                  } 
              col-${topContentColRatioLeft ? topContentColRatioLeft : '6'}`
                : `col-${topContentColRatioLeft ? topContentColRatioLeft : '6'}`
              : `col-12`
          }
        >
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
        {topContent && (
          <div
            className={
              topContent
                ? topContentColRatioRightMd
                  ? `col-md-${
                      topContentColRatioRightMd
                        ? topContentColRatioRightMd
                        : '6'
                    } 
            col-${topContentColRatioRight ? topContentColRatioRight : '6'}`
                  : `col-${
                      topContentColRatioRight ? topContentColRatioRight : '6'
                    }`
                : `col-12`
            }
          >
            {topContent}
          </div>
        )}
        {underSearchBarContent ? (
          <div className="col-12">{underSearchBarContent}</div>
        ) : null}
      </div>
      <br />
      <div style={{ overflowX: 'auto' }}>
        <table
          className={`table table-striped ${
            showClickableRows ? 'table-hover' : ''
          }`}
          {...getTableProps()}
        >
          <thead className="py-5">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {showMultiSelect && (
                  <th>
                    <div>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMultiSelectRows(getAllRowsID(data));
                          } else {
                            setMultiSelectRows([]);
                          }
                        }}
                      />
                    </div>
                  </th>
                )}
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {/* Add a sort direction indicator */}
                    <span className="ms-2">
                      {
                        column.isSorted ? (
                          column.isSortedDesc ? (
                            <i className="bi bi-caret-up-fill"></i>
                          ) : (
                            <i className="bi bi-caret-down-fill"></i>
                          )
                        ) : (
                          <i className="bi bi-caret-down-fill invisible"></i>
                        ) // just to occupy some space
                      }
                    </span>
                    {/* Render the columns filter UI */}
                    {/* <div className="py-2">
                    {column.canFilter ? column.render("Filter") : null}
                  </div> */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  style={{ cursor: showClickableRows ? 'pointer' : '' }}
                  title={tooltipText}
                >
                  {showMultiSelect && (
                    <td>
                      <div>
                        <input
                          type="checkbox"
                          checked={multiSelectRows.includes(row.original.id)}
                          onChange={() => {
                            setMultiSelectRows(
                              getUpdatedMultiSelectRows(
                                row.original.id,
                                multiSelectRows
                              )
                            );
                          }}
                        />
                      </div>
                    </td>
                  )}
                  {row.cells.map((cell, index, arr) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        onClick={
                          index < arr.length - 1
                            ? () => {
                                onRowClick(row.original.id);
                              }
                            : () => {}
                        }
                      >
                        {cell.render('Cell') ? cell.render('Cell') : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <br />
    </div>
  );
}

function FilterSortingTableComponent(props) {
  const columns = React.useMemo(() => [...props.columns], [props.columns]);
  return (
    <Table
      columns={columns}
      data={props.data}
      onRowClick={props.onRowClick}
      showClickableRows={props.showClickableRows}
      tooltipText={props.tooltipText}
      topContent={props.topContent}
      topContentAlignment={props.topContentAlignment}
      topContentColRatioLeft={props.topContentColRatioLeft}
      topContentColRatioRight={props.topContentColRatioRight}
      topContentColRatioLeftMd={props.topContentColRatioLeftMd}
      topContentColRatioRightMd={props.topContentColRatioRightMd}
      showMultiSelect={props.showMultiSelect}
      multiSelectRows={props.multiSelectRows}
      setMultiSelectRows={props.setMultiSelectRows}
      underSearchBarContent={props.underSearchBarContent}
    />
  );
}

export default FilterSortingTableComponent;
